import { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from 'react-redux';
import ChatHistory from "./Chat/ChatHistory";
import ChatInput from "./Chat/ChatInput";
import SuggestionsPanel from "./SuggestionPanel/SuggestionsPanel";
import AmplitudeSuggestionsLocked from "./SuggestionPanel/AmplitudeSuggestionsLocked";
import { getArtistInfo, sendMessage, refreshWorkspaceSession } from "../../../redux/actions";
import { toast } from "react-toastify";
import { showToast } from "../../../helper/ShowToast";
import { CreditPlanModal } from "../../../components/CreditPlanModal";
import { PricingPlanModal } from "../../../components/PricingPlanModal";
import { Link, useLocation, useNavigate } from "react-router-dom";
import AppTour from "../../../components/AppTour/AppTour";
import {
    canUseAmplitudeChat,
    getUserDisplayName,
    hasArtistLinked,
    shouldShowAmplitudeSuggestions,
} from "../../../helper/access";
import { amplitudeAISteps } from "../../../components/AppTour/ModuleTourSteps";

function AmplitudeAI() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { hash } = useLocation();
    const [showCreditPlanModal, setShowCreditPlanModal] = useState(false);
    const [showPricingPlanModal, setShowPricingPlanModal] = useState(false);
    const [runTour, setRunTour] = useState(false);
    const artistDetails = useSelector(state => state.artistDetails);
    const { artistInfo } = artistDetails;
    const reducerData = useSelector(state => state?.amplitudeReducer);
    const { messages, showLoad } = reducerData;
    const { creditDetails, hasActiveSubscription } = useSelector((state) => state.billing);
    const access = useSelector((state) => state.auth.access);
    const userDetails = useSelector((state) => state.auth.userDetails);

    const displayName = useMemo(
        () => getUserDisplayName(userDetails, artistInfo, access),
        [userDetails, artistInfo, access]
    );

    const showLinkArtistBanner = !hasArtistLinked(access, userDetails);
    const showRealSuggestions = shouldShowAmplitudeSuggestions(access, userDetails, {
        hasActiveSubscription,
    });
    const suggestionsLockVariant = hasArtistLinked(access, userDetails)
        ? "go_pro"
        : "link_artist";
    const creditsResolved = Boolean(creditDetails?.lastFetched);
    const creditsRemaining = creditDetails?.data?.remaining ?? 0;
    const creditsExhausted = creditsResolved && creditsRemaining <= 0;

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const status = params.get("status");
        if (!status) return;

        const handleCheckoutReturn = async () => {
            if (status === "success") {
                showToast("success", "Success! Your subscription has been activated.");
                await dispatch(refreshWorkspaceSession());
                dispatch(getArtistInfo(toast));
            }
            if (status === "cancel") {
                showToast("error", "Payment cancelled");
            }
        };

        handleCheckoutReturn();

        params.delete("status");
        const newUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : "");
        window.history.replaceState({}, "", newUrl);
    }, [dispatch]);

    useEffect(() => {
        if (hash === "#start-tour") {
            setRunTour(true);
        }
    }, [hash]);

    const handleSend = (input) => {
        if (access && !canUseAmplitudeChat(access)) {
            showToast("info", "Amplitude AI chat is not available for your account.");
            return;
        }
        if (creditsExhausted) return;

        const currentSelectedDate = sessionStorage.getItem('chatHistorySelectedDate');
        if (currentSelectedDate && currentSelectedDate !== 'today') {
            dispatch({ type: 'CLEAR_CHAT_HISTORY' });
            sessionStorage.setItem('chatHistorySelectedDate', 'today');
            sessionStorage.setItem('chatHistorySelectedRange', 'Today');
        }

        dispatch(sendMessage(toast, input));
    };

    const handleSuggestionAction = () => {
        alert("Suggestion are work in progress.");
    };

    return (
        <div className="amplitude-ai-page py-3">
            <div className="container">
                <AppTour
                    tourSteps={amplitudeAISteps}
                    runTour={runTour}
                    setRunTour={setRunTour}
                />

                {showLinkArtistBanner && (
                    <div className="mb-4 rounded-xl border border-theme_clr4/40 bg-theme_clr1/10 px-4 py-3 text-sm text-slate-700">
                        <span className="font-semibold text-theme_clr1">Limited data mode.</span>{" "}
                        Amplitude can still help with career advice.{" "}
                        <Link to="/link-accounts" className="text-theme_clr4 font-semibold hover:underline">
                            Link your artist
                        </Link>{" "}
                        for streaming stats and data-backed answers.
                    </div>
                )}

                <div className="flex flex-col lg:flex-row gap-4">
                    <div className="left-section flex flex-1 flex-col lg:h-[calc(100svh-100px)] overflow-auto">
                        <div data-tour="chat-history">
                            <ChatHistory
                                messages={messages}
                                isLoading={showLoad}
                            />
                        </div>

                        <div className="mt-3 shrink-0 w-full" data-tour="chat-input">
                            <ChatInput
                                title={`You're in control of your journey, ${displayName}.`}
                                inputPlaceholder="Ask your AI Assistant, Amplitude for help."
                                onSend={handleSend}
                                isDisabled={showLoad}
                                creditsExhausted={creditsExhausted}
                                onUpgradeClick={() => setShowPricingPlanModal(true)}
                                onGetCreditsClick={() => setShowCreditPlanModal(true)}
                            />
                        </div>
                    </div>

                    <div className="flex-[0_0_300px] lg:h-[calc(100svh-100px)]" data-tour="suggestions-panel">
                        {showRealSuggestions ? (
                            <SuggestionsPanel
                                onAction={handleSuggestionAction}
                                disabled={showLoad}
                            />
                        ) : (
                            <AmplitudeSuggestionsLocked
                                variant={suggestionsLockVariant}
                                onGoPro={() => setShowPricingPlanModal(true)}
                                onLinkArtist={() => navigate("/link-accounts")}
                            />
                        )}
                    </div>
                </div>

                <PricingPlanModal
                    isOpen={showPricingPlanModal}
                    onClose={() => setShowPricingPlanModal(false)}
                    returnUrl={window.location.origin + "/admin/amplitude-ai"}
                />
                <CreditPlanModal
                    isOpen={showCreditPlanModal}
                    onClose={() => setShowCreditPlanModal(false)}
                />
            </div>
        </div>
    );
}

export default AmplitudeAI;
